// src/auth/AuthProvider.jsx
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { normalizeRole } from "./permissions";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { PLATFORM_DISCLAIMER_VERSION } from "../config/platformDisclaimer";

const AuthCtx = createContext(null);

function buildBlockedMessage(reason) {
  if (reason === "USER_DISABLED")
    return "Access blocked: your account is disabled. Please contact an administrator.";
  if (reason === "MISSING_PROFILE")
    return "Access blocked: your user profile does not exist. Please contact an administrator.";
  if (reason === "MISSING_ORG")
    return "Access blocked: no organisation is assigned to your account.";
  if (reason === "ORG_DISABLED")
    return "Access blocked: your organisation is disabled. Please contact an administrator.";
  if (reason === "PERMISSION_DENIED")
    return "Access blocked: you are not authorized. Please contact an administrator.";
  return "Access blocked: unable to verify your access. Please contact an administrator.";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const [me, setMe] = useState(null);
  const [org, setOrg] = useState(null);

  // Fail closed: no role until the profile is loaded and recognised. The old default
  // of "staff" silently granted staff behaviour to unknown/missing roles.
  const [role, setRole] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  const [authLoading, setAuthLoading] = useState(true);

  // blockedReason = null means OK
  const [blockedReason, setBlockedReason] = useState(null);
  const [blockedMessage, setBlockedMessage] = useState("");

  const loadUserProfile = useCallback(async (u) => {
    const meSnap = await getDoc(doc(db, "users", u.uid));
    if (!meSnap.exists()) {
      const r = "MISSING_PROFILE";
      setBlockedReason(r);
      setBlockedMessage(buildBlockedMessage(r));
      return;
    }

    const meData = meSnap.data();
    setMe(meData);

    // ✅ SINGLE source of truth: me.role — normalised deny-by-default.
    // Unknown or legacy values (including the retired "staff") become "" = no privileges.
    const r = normalizeRole(meData?.role);
    setRole(r);
    const admin = r === "admin";
    setIsAdmin(admin);

    // user disabled?
    if (meData?.disabled === true) {
      const br = "USER_DISABLED";
      setBlockedReason(br);
      setBlockedMessage(buildBlockedMessage(br));
      return;
    }

    // Admin: no org required
    if (admin) {
      return;
    }

    // Staff/Manager: org required
    const oid = String(meData?.orgId || "").trim();
    if (!oid) {
      const br = "MISSING_ORG";
      setBlockedReason(br);
      setBlockedMessage(buildBlockedMessage(br));
      return;
    }

    const orgSnap = await getDoc(doc(db, "orgs", oid));
    const orgData = orgSnap.exists() ? orgSnap.data() : null;
    setOrg(orgData);

    const orgDisabled = orgData?.disabled === true;
    if (orgDisabled) {
      const br = "ORG_DISABLED";
      setBlockedReason(br);
      setBlockedMessage(buildBlockedMessage(br));
    }
  }, []);

  const refreshAuthProfile = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;

    setBlockedReason(null);
    setBlockedMessage("");
    setMe(null);
    setOrg(null);
    setRole("");
    setIsAdmin(false);
    await loadUserProfile(u);
  }, [loadUserProfile]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthLoading(true);

      // reset
      setUser(u || null);
      setMe(null);
      setOrg(null);
      setRole("");
      setIsAdmin(false);
      setBlockedReason(null);
      setBlockedMessage("");

      if (!u) {
        setAuthLoading(false);
        return;
      }

      try {
        // Force token propagation to Firestore before reading
        await u.getIdToken();
        await loadUserProfile(u);
        setAuthLoading(false);
      } catch (e) {
        const msg = String(e?.message || "");
        const br =
          msg.includes("Missing or insufficient permissions") ||
          e?.code === "permission-denied"
            ? "PERMISSION_DENIED"
            : "UNKNOWN";

        setBlockedReason(br);
        setBlockedMessage(buildBlockedMessage(br));
        setAuthLoading(false);
      }
    });

    return () => unsub();
  }, [loadUserProfile]);

  const mustAcceptDisclaimer =
    !!user &&
    !blockedReason &&
    me?.platformDisclaimerAccepted !== true;
  const mustAcceptCurrentDisclaimer =
    !!user &&
    !blockedReason &&
    (me?.platformDisclaimerAccepted !== true ||
      me?.platformDisclaimerVersion !== PLATFORM_DISCLAIMER_VERSION);

  return (
    <AuthCtx.Provider
      value={{
        user,
        me,
        org,
        role,
        isAdmin,
        authLoading,
        blockedReason,
        blockedMessage,
        mustAcceptDisclaimer,
        mustAcceptCurrentDisclaimer,
        platformDisclaimerVersion: PLATFORM_DISCLAIMER_VERSION,
        refreshAuthProfile,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
